import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { StatusBadge, ClientLogo } from "@/components/Layout";
import { ArrowLeft, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import Comments from "@/components/Comments";
import DatabasesPanel from "@/components/DatabasesPanel";
import ExportProjectPdfDialog from "@/components/ExportProjectPdfDialog";
import { STATUS_OPTIONS } from "@/lib/statuses";
import { fmtDate, fmtDateTime } from "@/lib/dates";
import TasksTab from "@/components/project/TasksTab";
import PagesTab from "@/components/project/PagesTab";
import FilesLinksTab from "@/components/project/FilesLinksTab";
import EditProjectDialog from "@/components/project/EditProjectDialog";
import TaskDetailDialog from "@/components/project/TaskDetailDialog";

export default function ProjectDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [client, setClient] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [pages, setPages] = useState([]);
  const [users, setUsers] = useState([]);

  const [activeTask, setActiveTask] = useState(null);
  const [editProjOpen, setEditProjOpen] = useState(false);

  const isAdminOrManager = ["Admin", "Manager"].includes(user.role);
  const isAdmin = user.role === "Admin";

  const load = async () => {
    try {
      const [p, t, pg, u] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get(`/tasks`, { params: { project_id: id, view: "all" } }),
        api.get(`/projects/${id}/pages`),
        api.get(`/users`).catch(() => ({ data: [] })),
      ]);
      setProject(p.data);
      setTasks(t.data || []);
      setPages(pg.data || []);
      setUsers(u.data || []);
      try {
        const c = await api.get(`/clients/${p.data.client_id}`);
        setClient(c.data);
      } catch (e) {
        // Executive may not have access to the client even though they have project access
        console.debug("clients/:id 403 — expected for Executive viewing unshared client", e?.response?.status);
      }
    } catch (e) {
      console.error("Failed to load project:", e);
      toast.error("Failed to load project");
    }
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const updateProject = async (changes) => {
    try {
      await api.patch(`/projects/${id}`, changes);
      load();
    } catch { toast.error("Failed"); }
  };

  const deleteProject = async () => {
    if (!window.confirm("Delete this project and all its tasks/pages?")) return;
    await api.delete(`/projects/${id}`);
    window.location.href = "/clients";
  };

  if (!project) return <div className="p-8 text-sm text-slate-500">Loading…</div>;

  return (
    <div className="min-h-screen bg-white" data-testid="project-detail-page">
      <div className="border-b border-slate-200">
        <div className="px-4 sm:px-8 py-6">
          <Link to={`/clients/${project.client_id}`} className="text-xs text-slate-500 inline-flex items-center gap-1 hover:text-[#0a2540]" data-testid="back-to-client">
            <ArrowLeft size={12} /> Back to client
          </Link>
          <div className="mt-3 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="min-w-0 flex-1 flex items-start gap-3 sm:gap-4">
              {client && <ClientLogo fileId={client.logo_file_id} name={client.name} size={44} />}
              <div className="min-w-0 flex-1">
                <div className="gc-label mb-1">
                  Project {client && (
                    <span className="ml-2 normal-case tracking-normal text-[10px] text-slate-500 font-mono-data">
                      · {client.name}
                    </span>
                  )}
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight break-words" style={{ fontFamily: "Outfit" }} data-testid="project-title">
                  {project.title}
                </h1>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <StatusBadge status={project.status} />
                  <span className="text-xs text-slate-500 font-mono-data">Due: {fmtDate(project.due_date)}</span>
                  {project.status === "Completed" && project.completed_at && (
                    <span
                      className="status-pill"
                      style={{ background: "#ecfdf5", borderColor: "#a7f3d0", color: "#047857" }}
                      data-testid="project-completed-on"
                    >
                      Completed {fmtDate(project.completed_at)}
                    </span>
                  )}
                  {project.stuck_with && project.stuck_with !== "None" && (
                    <span className="status-pill" style={{ background: "#fef3c7", borderColor: "#fbbf24", color: "#b45309" }}>
                      Stuck — {project.stuck_with}
                    </span>
                  )}
                  <span className="text-xs text-slate-500">{(project.assignee_ids || []).length} assignees</span>
                </div>
                {/* Progress bar */}
                <div className="mt-3 max-w-md">
                  <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1">
                    <span className="gc-label">Progress</span>
                    <span className="font-mono-data text-[#0a2540]">
                      {project.tasks_summary?.completed ?? 0}/{project.tasks_summary?.total ?? 0} tasks
                      <span className="text-slate-400"> · {project.progress_pct ?? 0}%</span>
                    </span>
                  </div>
                  <div className="h-1.5 bg-slate-100" data-testid="project-progress">
                    <div className="h-full bg-[#0a2540] transition-all" style={{ width: `${project.progress_pct ?? 0}%` }} />
                  </div>
                </div>
                {project.description && (
                  <p className="mt-4 text-sm text-slate-600 max-w-3xl leading-relaxed whitespace-pre-wrap">{project.description}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap shrink-0">
              <ExportProjectPdfDialog projectId={id} projectTitle={project.title} />
              {isAdminOrManager && (
                <Button variant="outline" className="rounded-sm" onClick={() => setEditProjOpen(true)} data-testid="btn-edit-project">
                  <Pencil size={14} className="mr-1.5" /> Edit
                </Button>
              )}
              {isAdmin && (
                <Button variant="outline" className="rounded-sm text-red-700 border-red-300 hover:bg-red-50" onClick={deleteProject} data-testid="btn-delete-project">
                  <Trash2 size={14} className="mr-1.5" /> Delete
                </Button>
              )}
            </div>
          </div>

          {/* Quick status update */}
          {(isAdminOrManager || (project.assignee_ids || []).includes(user.user_id)) && (
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="gc-label">Status</span>
                <Select value={project.status} onValueChange={(v) => updateProject({ status: v })}>
                  <SelectTrigger className="h-8 w-40 rounded-sm" data-testid="select-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <span className="gc-label">Stuck With</span>
                <Select value={project.stuck_with} onValueChange={(v) => updateProject({ stuck_with: v })}>
                  <SelectTrigger className="h-8 w-36 rounded-sm" data-testid="select-stuck"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="None">None</SelectItem>
                    <SelectItem value="Us">Us</SelectItem>
                    <SelectItem value="Client">Client</SelectItem>
                    <SelectItem value="External">External</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>
      </div>

      <Tabs defaultValue="tasks" className="px-4 sm:px-8 py-6">
        <TabsList className="bg-transparent border-b border-slate-200 rounded-none w-full justify-start h-auto p-0 overflow-x-auto flex-nowrap">
          <TabsTrigger value="tasks" className="shrink-0 rounded-none border-b-2 border-transparent data-[state=active]:border-[#0a2540] data-[state=active]:shadow-none pb-2 px-3 sm:px-4 text-xs sm:text-sm" data-testid="tab-tasks">Tasks ({tasks.filter((t) => t.status !== "Completed").length})</TabsTrigger>
          <TabsTrigger value="pages" className="shrink-0 rounded-none border-b-2 border-transparent data-[state=active]:border-[#0a2540] data-[state=active]:shadow-none pb-2 px-3 sm:px-4 text-xs sm:text-sm" data-testid="tab-pages">Pages ({pages.length})</TabsTrigger>
          <TabsTrigger value="files" className="shrink-0 rounded-none border-b-2 border-transparent data-[state=active]:border-[#0a2540] data-[state=active]:shadow-none pb-2 px-3 sm:px-4 text-xs sm:text-sm" data-testid="tab-files">Files & Links</TabsTrigger>
          <TabsTrigger value="databases" className="shrink-0 rounded-none border-b-2 border-transparent data-[state=active]:border-[#0a2540] data-[state=active]:shadow-none pb-2 px-3 sm:px-4 text-xs sm:text-sm" data-testid="tab-databases">Databases</TabsTrigger>
          <TabsTrigger value="comments" className="shrink-0 rounded-none border-b-2 border-transparent data-[state=active]:border-[#0a2540] data-[state=active]:shadow-none pb-2 px-3 sm:px-4 text-xs sm:text-sm" data-testid="tab-comments">Comments</TabsTrigger>
          <TabsTrigger value="overview" className="shrink-0 rounded-none border-b-2 border-transparent data-[state=active]:border-[#0a2540] data-[state=active]:shadow-none pb-2 px-3 sm:px-4 text-xs sm:text-sm" data-testid="tab-overview">Overview</TabsTrigger>
        </TabsList>

        <TabsContent value="tasks" className="pt-6">
          <TasksTab
            projectId={id}
            tasks={tasks}
            users={users}
            onOpenTask={setActiveTask}
            onChanged={load}
          />
        </TabsContent>

        <TabsContent value="pages" className="pt-6">
          <PagesTab scope="project" parentId={id} pages={pages} isAdmin={isAdmin} onChanged={load} />
        </TabsContent>

        <TabsContent value="files" className="pt-6">
          <FilesLinksTab
            projectId={id}
            files={project.files}
            driveLinks={project.drive_links}
            onChanged={load}
          />
        </TabsContent>

        <TabsContent value="databases" className="pt-6">
          <DatabasesPanel projectId={id} />
        </TabsContent>

        <TabsContent value="comments" className="pt-6">
          <div className="max-w-3xl">
            <Comments entityType="project" entityId={id} />
          </div>
        </TabsContent>

        <TabsContent value="overview" className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
            <div className="bg-white px-4 py-3">
              <div className="gc-label text-[10px]">To-Do / Scope</div>
              <div className="mt-1 text-sm text-[#0f172a] whitespace-pre-wrap">{project.todo || <span className="text-slate-300">—</span>}</div>
            </div>
            <div className="bg-white px-4 py-3">
              <div className="gc-label text-[10px]">Stuck Note</div>
              <div className="mt-1 text-sm text-[#0f172a] whitespace-pre-wrap">{project.stuck_note || <span className="text-slate-300">—</span>}</div>
            </div>
            <div className="bg-white px-4 py-3">
              <div className="gc-label text-[10px]">Assignees</div>
              <div className="mt-1 text-sm flex flex-wrap gap-1.5">
                {(project.assignee_ids || []).map((aid) => {
                  const u = users.find((x) => x.user_id === aid);
                  return <span key={aid} className="status-pill" style={{ background: "#f1f5f9", borderColor: "#cbd5e1", color: "#0a2540" }}>{u?.name || aid}</span>;
                })}
                {(project.assignee_ids || []).length === 0 && <span className="text-slate-300">—</span>}
              </div>
            </div>
            <div className="bg-white px-4 py-3">
              <div className="gc-label text-[10px]">Created</div>
              <div className="mt-1 text-sm font-mono-data text-[#0a2540]">{fmtDateTime(project.created_at)}</div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <EditProjectDialog
        open={editProjOpen}
        onOpenChange={setEditProjOpen}
        project={project}
        users={users}
        onSaved={load}
      />

      <TaskDetailDialog
        task={activeTask}
        users={users}
        client={client}
        canFullEdit={isAdminOrManager}
        canDelete={isAdmin}
        onClose={() => setActiveTask(null)}
        onChanged={load}
      />
    </div>
  );
}
